import { useEffect, useState } from 'react'

/**
 * Contexto que o Chatwoot entrega a um dashboard app.
 *
 * O handshake é por postMessage, não por querystring: o iframe avisa que está
 * pronto e o Chatwoot responde com a conversa e o contato. Fora do Chatwoot
 * (teste direto no navegador) aceita `?telefone=` como atalho.
 */
export type ContextoChatwoot = {
  /** O que será usado para procurar no acervo: telefone ou id do canal. */
  chave?: string
  /** De onde saiu a chave — aparece na tela quando nada é encontrado. */
  chaveOrigem?: 'telefone' | 'identificador'
  nome?: string

  /**
   * O id da conversa ABERTA no Chatwoot.
   *
   * 🔴 **Entrou na demanda 041, e a ausência dele era um defeito silencioso.**
   * O back aceitava `conversaExterna` desde a 014 e ninguém mandava: o painel
   * resolvia o contato pela chave e tudo funcionava — até precisar saber *qual*
   * conversa. Sem ele, "o que o assistente recebeu" mostraria a conversa mais
   * recente do contato, que não é necessariamente a que está na tela, e o
   * destaque do resumo sairia da marca errada quando o cliente falou com as duas.
   */
  conversaExterna?: number

  /**
   * E-mail do atendente logado no Chatwoot.
   *
   * 🔴 É a identidade que abre a sessão do painel, e ela só vale porque o
   * `event.origin` do `postMessage` é preenchido pelo NAVEGADOR — a página que
   * envia não consegue forjá-lo. É o oposto do cabeçalho `Origin` de uma
   * requisição HTTP, que um cliente fora do navegador simplesmente não manda.
   */
  agente?: string

  origem: 'chatwoot' | 'querystring' | 'aguardando'

  /**
   * 🔴 **O motivo para refazer a busca** (demanda 109).
   *
   * Sobe a cada evento que torna o que está na tela possivelmente velho: o
   * atendente trocou de conversa, voltou a esta aba, ou clicou em atualizar.
   *
   * ⭐ **Existe porque a troca de conversa não é o único jeito de o dado
   * envelhecer.** Mensagem nova na MESMA conversa não muda `chave` nem
   * `conversaExterna` — os painéis não refariam a busca, e o orçamento
   * calculado há um minuto não apareceria. Um contador que os três painéis
   * carregam nas dependências resolve os dois casos com a mesma peça.
   */
  versao: number
}

type Contato = {
  phone_number?: string | null
  name?: string | null
  identifier?: string | null
  additional_attributes?: Record<string, unknown> | null
}

type Agente = { email?: string | null; name?: string | null }

type CargaChatwoot = {
  data?: {
    contact?: Contato
    conversation?: { id?: number; meta?: { sender?: Contato; assignee?: Agente } }
    currentAgent?: Agente
  }
}

/**
 * O último contexto recebido, guardado fora do React.
 *
 * 🔴 O Chatwoot entrega o contexto **uma vez**, quando o iframe avisa que está
 * pronto. Quem monta depois — e a tela do painel monta depois da sessão abrir —
 * registra o seu ouvinte num momento em que a mensagem já passou, e fica
 * esperando para sempre.
 *
 * Sem isto, o painel autenticava e mostrava "aguardando o Chatwoot" eternamente.
 * Foi um teste que pegou; em produção teria virado "o painel não carrega".
 */
let ultimoContexto: ContextoChatwoot | null = null

/** Quem está montado agora e quer saber quando o contexto chegar. */
const assinantes = new Set<(c: ContextoChatwoot) => void>()

/**
 * Origens aceitas no postMessage (`ARQ-09`). Aceitar qualquer origem deixa
 * qualquer página que embuta o painel injetar um contato e ler o histórico dele
 * — e a mensagem chega igualzinha à do Chatwoot.
 *
 * Vale porque `event.origin` é preenchido pelo **navegador**: quem envia não
 * consegue forjá-lo.
 */
const origensPermitidas = (import.meta.env.VITE_ORIGENS_CHATWOOT ?? '')
  .split(',')
  .map((o: string) => o.trim())
  .filter(Boolean)

function aoReceber(evento: MessageEvent) {
  if (origensPermitidas.length > 0 && !origensPermitidas.includes(evento.origin)) return

  // O Chatwoot manda ora string JSON, ora objeto — aceitar os dois.
  let carga: CargaChatwoot | undefined
  try {
    carga = typeof evento.data === 'string' ? JSON.parse(evento.data) : evento.data
  } catch {
    return
  }

  const contato = carga?.data?.contact ?? carga?.data?.conversation?.meta?.sender
  if (!contato) return

  // Telefone quando existe; senão o identificador do canal. Telegram e
  // Instagram não fornecem número, e o acervo guarda o id do canal no mesmo
  // campo — 9 dígitos no Telegram, 16 no Instagram.
  //
  // Vale notar a assimetria: o id do Telegram é global por usuário, então casa
  // entre plataformas diferentes. O do Instagram é escopado por aplicativo —
  // nunca vai bater com o que a Zintech registrou.
  const telefone = limpar(contato.phone_number)
  const identificador = limpar(contato.identifier) ?? extrairId(contato.additional_attributes)

  const novo: ContextoChatwoot = {
    versao: versaoAtual,
    chave: telefone ?? identificador,
    chaveOrigem: telefone ? 'telefone' : identificador ? 'identificador' : undefined,
    nome: contato.name ?? undefined,

    // Number() protege contra o id vir como string — o Chatwoot já mandou as
    // duas formas em campos diferentes da mesma carga.
    conversaExterna: Number(carga?.data?.conversation?.id) || undefined,

    // O agente logado, se a plataforma o entregar. Sem ele o painel não abre
    // sessão — e diz isso, em vez de tentar e falhar em 403.
    agente: carga?.data?.currentAgent?.email ?? undefined,

    origem: 'chatwoot',
  }

  // ⚠️ **Só avisa quando algo MUDOU.**
  //
  // A insistência agora não tem fim (ver `agendar`), então o Chatwoot responde
  // de tempos em tempos com o mesmo conteúdo. Repassar cada resposta criaria um
  // objeto novo a cada ciclo, e os painéis — que dependem de `versao` — refariam
  // a busca para sempre, sem nada ter acontecido.
  const mudou = !ultimoContexto || assinaturaDe(novo) !== assinaturaDe(ultimoContexto)

  if (mudou) versaoAtual += 1

  const publicado = { ...novo, versao: versaoAtual }

  const primeiro = ultimoContexto === null
  ultimoContexto = publicado

  // Chegou o primeiro: sai da cadência rápida e passa a vigiar a visibilidade.
  // O custo tem teto; o comportamento, não (critério 6 da 109).
  if (primeiro) {
    agendar(frescos > 0 ? IntervaloDoPedido : IntervaloDeVigilia)
    observarVisibilidade()
  }

  // Quem pediu confirmação (282) recebe esta resposta, mudada ou não: o que ele quer
  // saber é o que o Chatwoot mostra AGORA.
  for (const responder of esperandoResposta) responder(publicado)
  esperandoResposta.clear()

  if (mudou) for (const avisar of assinantes) avisar(publicado)
}

/** O que, mudando, obriga os painéis a buscar de novo. */
function assinaturaDe(c: ContextoChatwoot) {
  return `${c.chave ?? ''}|${c.conversaExterna ?? ''}|${c.agente ?? ''}`
}

/**
 * 🔴 **O ouvinte é registrado AQUI, na avaliação do módulo — não num
 * `useEffect`** (demanda 062).
 *
 * O Chatwoot posta o contexto no `load` do iframe. Um `useEffect` roda depois
 * da primeira renderização, e em página pequena isso cai **depois** do `load`:
 * a mensagem passa sem ninguém ouvindo, e guardar o último contexto num módulo
 * não ajuda, porque não houve o que guardar.
 *
 * Módulo ES executa antes do `load`, sempre. A corrida fecha por construção, e
 * não por tempo de espera.
 *
 * ⚠️ Retentar o `fetch-info` NÃO seria correção: o Chatwoot responde a esse
 * pedido sempre no **primeiro** dashboard app (`onIframeLoad(0)`, fixo no código
 * dele). Era por isso que só a aba Histórico se recuperava.
 */
/**
 * As duas cadências (065, revistas pela 109).
 *
 * ⭐ **O teto mudou de lugar: era do COMPORTAMENTO, virou do CUSTO.**
 *
 * A 065 pedia a cada 2 s e parava aos 60 s, porque *"um painel aberto o dia
 * inteiro não pode repetir para sempre"*. A intenção estava certa e o efeito
 * era outro: passados os 60 s, trocar de conversa deixava o painel com o
 * contexto **anterior**, para sempre — e o pior é que ele some quando alguém
 * vai conferir, porque quem abre o Chatwoot e troca de conversa logo vê tudo
 * certo. Quem deixa a aba aberta a manhã inteira — o uso real — vê o histórico
 * do cliente **errado, com cara de certo**.
 *
 * Agora: rápido enquanto não há contexto; devagar para sempre depois. O custo
 * fica pequeno; a recuperação nunca acaba.
 */
const IntervaloDoPedido = 2_000
const IntervaloDeVigilia = 15_000

let laco: ReturnType<typeof setInterval> | undefined

/** Quantos painéis montados pediram a cadência rápida (282). */
let frescos = 0

/** Quem está esperando a PRÓXIMA resposta do Chatwoot, mudada ou não (282). */
const esperandoResposta = new Set<(c: ContextoChatwoot) => void>()

/**
 * 🔴 **A cadência rápida enquanto o painel de ENVIO está montado** (282, revisão).
 *
 * A vigília de 15 s basta para quem LÊ: um histórico velho por alguns segundos se corrige
 * sozinho. Para quem ENVIA, não: o atendente escolhe o arquivo na conversa A, clica na B,
 * e em 15 s o painel ainda acha que está na A. Enquanto a aba "Enviar arquivo" existe, o
 * pedido volta a cada 2 s; ao desmontar, a vigília volta. Devolve quem desliga.
 */
export function manterContextoFresco(): () => void {
  frescos += 1
  agendar(IntervaloDoPedido)

  return () => {
    frescos -= 1
    if (frescos === 0) agendar(ultimoContexto ? IntervaloDeVigilia : IntervaloDoPedido)
  }
}

/**
 * 🔴 **Pergunta ao Chatwoot, no instante do envio, qual conversa está aberta** (282).
 *
 * Nem a cadência de 2 s fecha a janela entre o clique na outra conversa e o clique em
 * "Enviar". Aqui o pedido sai na hora, e a promessa resolve com a próxima resposta —
 * mudada ou não. Sem resposta no prazo, resolve `null`: quem chama recusa o envio, porque
 * não saber qual conversa está aberta não é motivo para mandar arquivo a um cliente.
 */
export function confirmarContexto(prazoMs = 3_000): Promise<ContextoChatwoot | null> {
  return new Promise((resolver) => {
    const responder = (c: ContextoChatwoot) => {
      clearTimeout(relogio)
      resolver(c)
    }

    const relogio = setTimeout(() => {
      esperandoResposta.delete(responder)
      resolver(null)
    }, prazoMs)

    esperandoResposta.add(responder)
    pedirContexto()
  })
}

/**
 * O contador que os painéis observam. Vive no módulo porque o ouvinte também
 * vive — ver a nota do `ultimoContexto`.
 */
let versaoAtual = 0

function pedirContexto() {
  window.parent?.postMessage('chatwoot-dashboard-app:fetch-info', '*')
}

function agendar(intervalo: number) {
  if (laco !== undefined) clearInterval(laco)
  laco = setInterval(pedirContexto, intervalo)
}

/**
 * 🔴 **Refazer a busca AGORA** — o botão de atualizar, e o que o observador de
 * visibilidade chama (demanda 109).
 *
 * Pede o contexto **e** sobe a versão sem esperar resposta: mensagem nova na
 * mesma conversa não muda `chave` nem `conversaExterna`, então a assinatura do
 * contexto seria idêntica e nada seria avisado. Quem pede para atualizar quer o
 * dado refeito, não a confirmação de que a conversa é a mesma.
 */
export function atualizarAgora() {
  pedirContexto()

  versaoAtual += 1

  const publicado: ContextoChatwoot = ultimoContexto
    ? { ...ultimoContexto, versao: versaoAtual }
    : { origem: 'aguardando', versao: versaoAtual }

  ultimoContexto = publicado

  for (const avisar of assinantes) avisar(publicado)
}

/**
 * 🔴 **Pedir uma vez não basta** (065).
 *
 * O Chatwoot só atende o pedido de contexto **enquanto aquele painel está
 * visível** — `triggerEvent(e) { this.isVisible && … }`, no código dele. Nós
 * pedíamos uma única vez, na avaliação do módulo, quando a aba ainda não é a
 * ativa: o pedido caía no vazio e **não havia segunda chance**, porque o
 * Chatwoot não reenvia por conta própria.
 *
 * Era por isso que só o Histórico funcionava — por acidente de ordem, era a aba
 * que o atendente abria primeiro.
 *
 * Repetir é a única via, porque quem decide o momento é o outro lado: não existe
 * evento, dentro do iframe, que diga "agora estou visível". O teto existe para
 * um painel aberto o dia inteiro não repetir para sempre.
 */
/**
 * 🔴 **`visibilitychange` NÃO serve, e isto foi MEDIDO** (109 §4.1).
 *
 * O `ConversationBox.vue` do Chatwoot usa `v-show` nos painéis — `display:
 * none`. O iframe **continua montado**, apenas escondido: não é destruído nem
 * recarregado ao trocar de aba. E a visibilidade de um documento dentro de
 * iframe segue o **contexto de navegação do topo** — a aba do NAVEGADOR —, não
 * o `display` do elemento que o contém. O evento simplesmente não dispara.
 *
 * ⭐ **O sinal que existe é o TAMANHO.** Escondido por `display: none`, o
 * documento deixa de intersectar e fica com dimensão zero. Voltar a intersectar
 * é exatamente o instante em que o `Frame.vue` do Chatwoot volta a atender o
 * `fetch-info` (`triggerEvent(e) { if (!this.isVisible) return }`) — então
 * pedir aqui é pedir no único momento em que a pergunta é respondida.
 *
 * 🔴 **Só é armado DEPOIS que o primeiro contexto chega**, e isso não é
 * detalhe. Durante a abertura o observador notifica duas vezes — antes e depois
 * do primeiro layout —, e a segunda pareceria uma "volta à aba". O painel
 * buscaria de novo em toda abertura: pequeno, mas é a diferença entre atualizar
 * por um motivo e atualizar por um ruído. Antes do primeiro contexto quem cobre
 * é a cadência rápida, que existe exatamente para isso.
 *
 * ⚠️ E a primeira notificação depois de armado também é descartada: `visivel`
 * começa `null`, e só a transição a partir de `false` conta.
 */
let observando = false

function observarVisibilidade() {
  if (observando || typeof IntersectionObserver === 'undefined') return

  observando = true

  let visivel: boolean | null = null

  const observador = new IntersectionObserver((entradas) => {
    for (const entrada of entradas) {
      const agora = entrada.isIntersecting

      // ⚠️ **A PRIMEIRA notificação é descartada, seja qual for.**
      //
      // O observador sempre notifica o estado inicial, e ele pode chegar como
      // "não intersecta" antes do primeiro layout. Tratar isso como uma volta
      // dispararia uma busca a mais em toda abertura de painel — pequena, mas
      // é a diferença entre atualizar por um motivo e atualizar por um ruído.
      //
      // Só a TRANSIÇÃO de escondido para visível é motivo de atualizar.
      if (visivel === false && agora) atualizarAgora()

      visivel = agora
    }
  })

  observador.observe(document.documentElement)
}

if (typeof window !== 'undefined') {
  window.addEventListener('message', aoReceber)

  pedirContexto()
  agendar(IntervaloDoPedido)
}

export function useContextoChatwoot(): ContextoChatwoot {
  const [ctx, setCtx] = useState<ContextoChatwoot>(() => {
    // Quem monta depois começa de onde o anterior parou.
    if (ultimoContexto) return ultimoContexto

    const p = new URLSearchParams(window.location.search).get('telefone')
    return p
      ? { chave: p, chaveOrigem: 'telefone', origem: 'querystring', versao: versaoAtual }
      : { origem: 'aguardando', versao: versaoAtual }
  })

  useEffect(() => {
    // Chegou entre o `useState` e este efeito? Pega o que já está guardado.
    if (ultimoContexto) setCtx(ultimoContexto)

    assinantes.add(setCtx)
    return () => { assinantes.delete(setCtx) }
  }, [])

  return ctx
}

function limpar(v: string | null | undefined) {
  const t = v?.trim()
  return t ? t : undefined
}

/** Alguns canais guardam o id do usuário nos atributos adicionais. */
function extrairId(attrs: Record<string, unknown> | null | undefined) {
  if (!attrs) return undefined

  for (const chave of ['user_id', 'telegram_id', 'chat_id', 'sender_id', 'social_profiles']) {
    const v = attrs[chave]
    if (typeof v === 'string' || typeof v === 'number') return String(v)
  }

  return undefined
}
